import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual } from 'react-redux';

import sameArrayResult from 'editor/src/store/design/util/sameArrayResult';
import { getPersonalizationTextUuids } from 'editor/src/store/design/util/sharedTextFields';
import { RootState } from 'editor/src/store/index';

export interface SharedTextFieldTarget {
  uuid: number;
  name: string;
  /** The spread holding it, so "Name" on the back of the card is tellable from
   *  "Name" on the front in what is otherwise a flat list of identical names. */
  spreadName: string;
  /** What the page picker groups by. The name is what the author reads, but two
   *  spreads may carry the same one — a book's pages routinely do — so the
   *  choice itself is made on the index. */
  spreadIndex: number;
  sharedFieldId: string | undefined;
  /** Keeps a string per picklist option, which a shared value cannot coexist
   *  with. Offered but closed, so the author sees the layer and is told why —
   *  the same treatment `blockedConditionOptions` gives the mirror case. */
  variesByPicklist: boolean;
  /** Whether this layer may be tied to another one: an unlocked text the
   *  spread's condition group actually offers the buyer an input for, and not
   *  one printing a code-set column (production fills that per unit).
   *
   *  Layers that cannot are listed anyway, because a group can still HOLD one —
   *  locked after it joined, or imported that way — and a member the panel
   *  cannot name is a group the author can neither see nor leave. */
  canJoin: boolean;
}

// Most designs share nothing; one shared array for that case keeps the empty
// result referentially stable. See getDesignConditions for the same trick.
const NONE: SharedTextFieldTarget[] = [];

const selectSpreads = (state: RootState) => state.design.designData?.spreads;

// The previous array comes back whenever no name, page or grouping moved, which
// is what the `shallowEqual` in the calling panel relies on — and the panel is
// instantiated once per personalisable text. See sameArrayResult for why the
// comparison is field by field and why it is array-guarded.
const sameEntries = sameArrayResult<SharedTextFieldTarget>(shallowEqual);

/**
 * Every text layer in the design, in design order, each carrying whether a
 * field may be tied to it.
 *
 * Two different questions are asked of this list and they need different
 * answers, which is why `canJoin` is a flag rather than a filter:
 *
 * - "what can this text be tied to?" — only layers the spread's condition group
 *   actually offers the buyer an input for, and only unlocked ones. Offering a
 *   decorative layer would let the author build a group whose lead the buyer's
 *   panel never draws, and the field would disappear from the page (see
 *   getSharedTextFieldLeadUuid).
 * - "what is this text tied to today?" — whatever the design says, candidate or
 *   not. Filtering that question through the first one is what let a member
 *   locked after it joined read as "not sharing" while the model went on
 *   sharing: the picklist control would open, the operation would refuse, and
 *   the way out would be invisible.
 *
 * Design-wide on purpose: the point of a shared field is to reach the OTHER
 * side of the card, so a list limited to the current spread would offer only
 * the pairings that are of no use.
 *
 * `canJoin` is candidacy, not a promise that the join is loss-free. Hidden is
 * deliberately not part of it — a text under a picklist option nobody has
 * picked yet is a legitimate thing to tie a field to, and refusing it would
 * make the offer depend on which option happens to be showing. The cost is that
 * the join's truncation reads the group's cap, which leaves hidden members out:
 * tie a 20-character front to a hidden 5-character back and the value is
 * adopted whole, then the back's cap enters the union the moment its option is
 * picked. Unlike the reflow-row version of this (see resolveSharedTextField),
 * it does not correct itself — the option stays picked — but the value is the
 * buyer's own and shortening it is the fix.
 */
const getSharedTextFieldTargets = createSelector(
  [selectSpreads],
  (spreads): SharedTextFieldTarget[] => {
    const targets = (spreads ?? []).flatMap((spread, spreadIndex) => {
      const personalizable = getPersonalizationTextUuids(spread);

      return spread.pages.flatMap((page) =>
        (page.groups.media ?? []).flatMap((element) =>
          element.type === 'text'
            ? [
                {
                  uuid: element.uuid,
                  name: element.name,
                  spreadName: spread.name,
                  spreadIndex,
                  sharedFieldId: element.extra.sharedFieldId,
                  variesByPicklist: !!element.extra.textVariants,
                  canJoin:
                    !element.personalizationLocked && !element.extra.variableColumn && personalizable.has(element.uuid),
                },
              ]
            : [],
        ),
      );
    });

    return targets.length ? targets : NONE;
  },
  // Held by the selector instance, not by the module: the recompute above runs
  // on every keystroke (its input is `spreads`), and without this the fresh
  // wrappers would defeat the caller's `shallowEqual` and re-render every text
  // panel in the list. reselect hands back the previous array when the entries
  // still match field for field.
  { memoizeOptions: { resultEqualityCheck: sameEntries } },
);

export default getSharedTextFieldTargets;
